import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { collection, doc, query, where, orderBy, onSnapshot, updateDoc, writeBatch, setDoc, serverTimestamp, limit } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../hooks/AuthContext';
import { trackRead } from '../services/firestoreMonitor';

const NotificationContext = createContext();

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);

  // Subscribe to user's notifications from Firestore
  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      return;
    }

    // User-specific notifications
    const qUser = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    // Global notifications (visible to all)
    const qGlobal = query(
      collection(db, 'notifications'),
      where('global', '==', true),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    let userNotifs = [];
    let globalNotifs = [];

    const mergeAndSet = () => {
      const seen = new Set();
      const merged = [...userNotifs, ...globalNotifs]
        .filter(n => { if (seen.has(n.id)) return false; seen.add(n.id); return true; })
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
        .slice(0, 50);
      setNotifications(merged);
    };

    const unsub1 = onSnapshot(qUser, (snapshot) => {
      trackRead('notifications (user)', snapshot.size, 'snapshot');
      userNotifs = snapshot.docs.map(d => ({
        id: d.id, ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      }));
      mergeAndSet();
    }, () => {});

    const unsub2 = onSnapshot(qGlobal, (snapshot) => {
      trackRead('notifications (global)', snapshot.size, 'snapshot');
      globalNotifs = snapshot.docs.map(d => ({
        id: d.id, ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      }));
      mergeAndSet();
    }, () => {});

    return () => { unsub1(); unsub2(); };
  }, [user?.uid]);

  const markAsRead = useCallback(async (id) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { read: true });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      const batch = writeBatch(db);
      notifications.filter(n => !n.read).forEach(n => {
        batch.update(doc(db, 'notifications', n.id), { read: true });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [notifications]);

  const clearAll = useCallback(async () => {
    try {
      const batch = writeBatch(db);
      notifications.forEach(n => {
        batch.delete(doc(db, 'notifications', n.id));
      });
      await batch.commit();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }, [notifications]);

  const addNotification = useCallback(async (notification) => {
    if (!user?.uid) return;
    try {
      const notifId = `notif-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      await setDoc(doc(db, 'notifications', notifId), {
        ...notification,
        userId: user.uid,
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  }, [user?.uid]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, addNotification, markAsRead, markAllRead, clearAll }}>
      {children}
    </NotificationContext.Provider>
  );
};

// Utility: create notification for another user
export const createNotification = async (targetUserId, notification) => {
  try {
    const notifId = `notif-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    await setDoc(doc(db, 'notifications', notifId), {
      ...notification,
      userId: targetUserId,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error creating notification:', error);
  }
};
